import React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable, runInAction } from 'mobx';
import { ModalButtonType, type ModalButtonOptions, type ModalDialogOptions, ModalWindow, type IModalDialogContent } from '@components/Modal';
import { apiService } from '@services/ApiService';
import { notificationService } from '@services/NotificationService';

export type NoteHistoryDialogProps = {
  noteId: string;
};

type HistorySnapshotSummary = {
  id: string;
  createdAt: Date;
  createdByUser: boolean;
  name: string;
  diffSummary?: string | null;
};

type HistorySnapshotDetail = {
  id: string;
  name: string;
  content: string;
  createdAt: Date;
  createdByUser: boolean;
};

@observer
export default class NoteHistoryDialog extends React.Component<NoteHistoryDialogProps> implements IModalDialogContent<void> {
  @observable private _snapshots: HistorySnapshotSummary[] = [];
  @observable private _isLoading = true;
  @observable private _viewingSnapshot: HistorySnapshotDetail | null = null;

  constructor(props: NoteHistoryDialogProps){
    super(props);
    makeObservable(this);
  }

  async componentDidMount(){
    await this._loadSnapshots();
  }

  public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    const buttons: ModalButtonOptions<void>[] = [
      {
        type: ModalButtonType.Cancel,
        onClick: () => {
          window.close();
        }
      }
    ];

    return {
      title: 'Note History',
      buttons,
      width: 800
    };
  }

  private async _loadSnapshots(): Promise<void>{
    runInAction(() => { this._isLoading = true; });
    try {
      const data = await apiService.get<{ snapshots: HistorySnapshotSummary[] }>(`/api/note/${this.props.noteId}/history`);
      runInAction(() => { this._snapshots = data.snapshots; });
    } finally {
      runInAction(() => { this._isLoading = false; });
    }
  }

  @action.bound
  private async _viewSnapshot(snapshot: HistorySnapshotSummary){
    // Open snapshot in a new tab with read-only editor view using the snapshot API endpoint
    const snapshotUrl = `/snapshot/${this.props.noteId}/${snapshot.id}`;
    const newTab = window.open(snapshotUrl, '_blank');
    
    if (!newTab) {
      notificationService.error('Please allow popups to view snapshots');
    }
  }

  @action.bound
  private async _restoreSnapshot(snapshotId: string, snapshotName: string){
    const confirmed = window.confirm(`Restore to "${snapshotName}"?\n\nCurrent content will be saved as a snapshot before restoring.`);
    if (!confirmed) return;

    await apiService.post<{ safetySnapshotId: string }>(`/api/note/${this.props.noteId}/history/${snapshotId}/restore`);
    notificationService.success('Note restored successfully');
    window.location.reload();
  }

  @action.bound
  private async _createSnapshot(){
    await apiService.post(`/api/note/${this.props.noteId}/history`);
    notificationService.success('Snapshot created');
    await this._loadSnapshots();
  }

  @action.bound
  private async _deleteSnapshot(snapshotId: string){
    const confirmed = window.confirm('Delete this snapshot? This action cannot be undone.');
    if (!confirmed) return;

    await apiService.delete(`/api/note/${this.props.noteId}/history/${snapshotId}`);
    await this._loadSnapshots();
    notificationService.success('Snapshot deleted');
  }

  private _formatDate(date: Date): string {
    return new Date(date).toLocaleString();
  }

  render(): React.ReactNode{
    if (this._isLoading && !this._viewingSnapshot) {
      return <div className="modal-loading">Loading history...</div>;
    }

    return (
      <div className="note-history-dialog">
        <div className="history-list-container">
          <div className="history-header">
            <h3>History</h3>
            <button 
              className="btn btn-sm btn-primary" 
              onClick={this._createSnapshot}
            >
              Create Snapshot
            </button>
          </div>
          {this._snapshots.length === 0 && !this._isLoading ? (
            <p>No history snapshots found.</p>
          ) : (
            <table className="history-table">
              <thead>
                <tr>
                  <th>Date/Time</th>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Change</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {this._snapshots.map(snap => (
                  <tr key={snap.id}>
                    <td>{this._formatDate(snap.createdAt)}</td>
                    <td>{snap.createdByUser ? 'Manual' : 'Auto'}</td>
                    <td>{snap.name || '(Untitled)'}</td>
                    <td>{snap.diffSummary || '-'}</td>
                    <td className="history-actions">
                      <button 
                        className="btn btn-sm btn-primary" 
                        onClick={() => this._viewSnapshot(snap)}
                      >
                        View
                      </button>
                      <button 
                        className="btn btn-sm btn-success" 
                        onClick={() => this._restoreSnapshot(snap.id, snap.name)}
                      >
                        Restore
                      </button>
                      {snap.createdByUser && (
                        <button 
                          className="btn btn-sm btn-danger" 
                          onClick={() => this._deleteSnapshot(snap.id)}
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {this._viewingSnapshot && (
          <div className="history-view-container">
            <div className="history-view-header">
              <h3>Viewing Snapshot: {this._formatDate(this._viewingSnapshot.createdAt)}</h3>
              <button 
                className="btn btn-sm btn-secondary" 
                onClick={() => runInAction(() => { this._viewingSnapshot = null; })}
              >
                Back to List
              </button>
            </div>
            <div className="history-view-content">
              <p><strong>Name:</strong> {this._viewingSnapshot.name || '(Untitled)'}</p>
              <p><strong>Type:</strong> {this._viewingSnapshot.createdByUser ? 'Manual' : 'Automatic'}</p>
              <p><strong>Content length:</strong> {this._viewingSnapshot.content.length} characters</p>
            </div>
          </div>
        )}
      </div>
    );
  }
}
